import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi, apiPost, Cargando, ErrorApi, formatFecha, confirmar } from '../shared/ui.tsx';
import { formatoRango } from '../shared/horas.ts';
import { apiFetch } from '../shared/api.ts';
import { useAuth } from '../shared/AuthContext.tsx';
import type { Salon } from '../shared/tipos.ts';

type Detalle = {
  id: string; fecha: string; hora_inicio: string; hora_fin: string; salon_id: string; estado: string; descripcion: string | null;
  puestos: Array<{ categoria_id: string; cantidad: number; cubiertos: number; categoria_nombre: string; personas_seleccionadas: string | null }>;
  salon: { nombre: string; direccion: string | null; notas: string | null };
  convocatorias: Array<{
    convocatoria_id: string; persona_id: string; categoria_id: string;
    persona_nombre: string; persona_apellido: string; estado: string;
    respondida_en: string | null;
  }>;
};

type Calif = { id: string; calificacion: number; comentario: string | null; creada_en: string };

type PersonaDisponible = {
  id: string; nombre: string; apellido: string; calificacion: number;
  disponible: boolean; motivo: string;
};

function estadoBadge(estado: string): { texto: string; clase: string } {
  switch (estado) {
    case 'aceptada': return { texto: 'Confirmado', clase: 'badge-ok' };
    case 'enviada': return { texto: 'Pendiente', clase: 'badge-pendiente' };
    case 'rechazada': return { texto: 'Rechazado', clase: 'badge-error' };
    case 'vencida': return { texto: 'Vencido', clase: 'badge-neutral' };
    case 'cancelada': return { texto: 'Cancelado', clase: 'badge-neutral' };
    case 'no_show': return { texto: 'No show', clase: 'badge-error' };
    default: return { texto: estado, clase: 'badge-neutral' };
  }
}

function Icono({ d, className = 'h-5 w-5' }: { d: string; className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

export function DetalleSolicitudPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const { datos, cargando, error, recargar } = useApi<Detalle>(`/api/solicitudes/${id}`);
  const califRes = useApi<Calif>(`/api/solicitudes/${id}/calificacion`);
  const califPersonas = useApi<Array<{ convocatoria_id: string; puntaje: number }>>(`/api/solicitudes/${id}/calificaciones-personal`);
  const salonesRes = useApi<Salon[]>('/api/salones');
  const [cal, setCal] = useState(0);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({ salon_id: '', fecha: '', hora_inicio: '', hora_fin: '', descripcion: '', puestos: [] as Array<{ categoria_id: string; cantidad: number; personas_seleccionadas: string[] }> });
  const [personasPorCategoria, setPersonasPorCategoria] = useState<Record<string, PersonaDisponible[]>>({});
  const [cargandoPersonas, setCargandoPersonas] = useState(false);
  const [acordeonExpandido, setAcordeonExpandido] = useState<string | null>(null);

  async function enviarCalificacion(e: FormEvent) {
    e.preventDefault();
    if (cal < 1 || cal > 5) { alert('Elegí 1 a 5 estrellas'); return; }
    setEnviando(true);
    try {
      await apiPost(`/api/solicitudes/${id}/calificar`, { calificacion: cal, comentario });
      califRes.recargar();
    } catch (e2) { alert(e2 instanceof Error ? e2.message : 'Error'); }
    finally { setEnviando(false); }
  }

  async function cargarPersonas(categoriaId: string, fecha: string, horaInicio: string, horaFin: string) {
    try {
      const params = new URLSearchParams({ categoria_id: categoriaId, fecha, hora_inicio: horaInicio, hora_fin: horaFin });
      const data = await apiFetch<{ personas: PersonaDisponible[] }>(`/api/personal/disponibles?${params}`);
      setPersonasPorCategoria(prev => ({ ...prev, [categoriaId]: data.personas }));
    } catch {
      setPersonasPorCategoria(prev => ({ ...prev, [categoriaId]: [] }));
    }
  }

  async function iniciarEdicion() {
    if (!datos) return;
    const puestosConSeleccion = datos.puestos.map(p => ({
      categoria_id: p.categoria_id,
      cantidad: p.cantidad,
      personas_seleccionadas: p.personas_seleccionadas ? JSON.parse(p.personas_seleccionadas) : [],
    }));
    setForm({
      salon_id: datos.salon_id,
      fecha: datos.fecha,
      hora_inicio: datos.hora_inicio,
      hora_fin: datos.hora_fin,
      descripcion: datos.descripcion || '',
      puestos: puestosConSeleccion,
    });
    setEditando(true);
    setCargandoPersonas(true);
    for (const p of datos.puestos) {
      await cargarPersonas(p.categoria_id, datos.fecha, datos.hora_inicio, datos.hora_fin);
    }
    setCargandoPersonas(false);
  }

  function togglePersona(categoriaId: string, personaId: string) {
    setForm(prev => ({
      ...prev,
      puestos: prev.puestos.map(p => {
        if (p.categoria_id !== categoriaId) return p;
        const seleccionadas = p.personas_seleccionadas.includes(personaId)
          ? p.personas_seleccionadas.filter(id => id !== personaId)
          : [...p.personas_seleccionadas, personaId];
        return { ...p, personas_seleccionadas: seleccionadas };
      }),
    }));
  }

  async function guardarEdicion(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    try {
      await apiPost(`/api/solicitudes/${id}`, form, 'PUT');
      setEditando(false);
      recargar();
    } catch (e2) { alert(e2 instanceof Error ? e2.message : 'Error'); }
    finally { setEnviando(false); }
  }

  if (cargando) return <Cargando />;
  if (error) return <ErrorApi mensaje={error} onReintentar={recargar} />;
  if (!datos) return null;

  const puedeEditar = datos.estado !== 'cancelada' && datos.estado !== 'en_curso' && datos.estado !== 'finalizada';
  const puedeCancelar = puedeEditar;

  async function cancelar() {
    if (!confirmar('¿Estás seguro de que querés cancelar esta solicitud? Esta acción no se puede deshacer.')) return;
    try {
      await apiPost(`/api/solicitudes/${id}/cancelar`, undefined, 'POST');
      recargar();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    }
  }

  const convocatoriasPorCategoria = new Map<string, typeof datos.convocatorias>();
  for (const c of datos.convocatorias) {
    const lista = convocatoriasPorCategoria.get(c.categoria_id) || [];
    lista.push(c);
    convocatoriasPorCategoria.set(c.categoria_id, lista);
  }

  const totalConfirmados = datos.convocatorias.filter(c => c.estado === 'aceptada').length;
  const totalPersonas = datos.puestos.reduce((a, p) => a + p.cantidad, 0);
  const pctAvance = totalPersonas > 0 ? Math.round((totalConfirmados / totalPersonas) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-neutro-800">Detalle de solicitud</h2>
          <p className="text-sm text-neutro-500 mt-0.5">{formatFecha(datos.fecha)} · {datos.salon.nombre}</p>
        </div>
        <button className="text-sm font-medium text-neutro-500 hover:text-neutro-700 transition-colors" onClick={() => navigate('/cliente')}>
          ← Volver
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-5">
        {/* Main column */}
        <div className="flex-1 space-y-5">
          {/* Info bento grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { icon: 'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5', label: 'Fecha', value: formatFecha(datos.fecha) },
              { icon: 'M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z', label: 'Horario', value: formatoRango(datos.hora_inicio, datos.hora_fin) },
              { icon: 'M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z', label: 'Salón', value: datos.salon.nombre, full: true },
            ].map((item) => (
              <div key={item.label} className={`rounded-xl border border-contorno-var bg-white p-4 ${item.full ? 'col-span-2 sm:col-span-3' : ''}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icono d={item.icon} className="h-4 w-4 text-primario-400" />
                  <p className="text-xs font-bold uppercase tracking-wide text-neutro-400">{item.label}</p>
                </div>
                <p className="text-sm font-semibold text-neutro-800">{item.value}</p>
                {item.label === 'Salón' && datos.salon.direccion && <p className="text-xs text-neutro-500 mt-1">{datos.salon.direccion}</p>}
              </div>
            ))}
          </div>

          {datos.descripcion && (
            <div className="rounded-xl border border-contorno-var bg-white p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icono d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" className="h-4 w-4 text-primario-400" />
                <p className="text-xs font-bold uppercase tracking-wide text-neutro-400">Descripción</p>
              </div>
              <p className="text-sm text-neutro-700">{datos.descripcion}</p>
            </div>
          )}

          {/* Personal accordion */}
          <div className="rounded-2xl border border-contorno-var bg-white overflow-hidden">
            <div className="px-5 pt-5 pb-3">
              <h3 className="text-base font-semibold text-neutro-800">Personal pedido</h3>
              <p className="text-xs text-neutro-400 mt-0.5">{totalConfirmados}/{totalPersonas} confirmados · {pctAvance}%</p>
              <div className="mt-3 progress-track">
                <div className="progress-fill" style={{ width: `${pctAvance}%` }} />
              </div>
            </div>
            <ul>
              {datos.puestos.map((p) => {
                const asignados = convocatoriasPorCategoria.get(p.categoria_id) || [];
                const confirmados = asignados.filter(a => a.estado === 'aceptada').length;
                const tieneSeleccion = p.personas_seleccionadas && JSON.parse(p.personas_seleccionadas).length > 0;
                const faltan = p.cantidad - asignados.length;
                const expandido = acordeonExpandido === p.categoria_id;
                return (
                  <li key={p.categoria_id} className="acordeon-item" data-active={expandido}>
                    <button
                      className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-superficie transition-colors"
                      onClick={() => setAcordeonExpandido(expandido ? null : p.categoria_id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${faltan === 0 ? 'bg-green-50' : 'bg-amber-50'}`}>
                          <Icono d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" className={`h-4 w-4 ${faltan === 0 ? 'text-estado-ok' : 'text-estado-pendiente'}`} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-neutro-800">{p.categoria_nombre}</p>
                          <p className="text-xs text-neutro-400">{confirmados}/{p.cantidad} confirmados</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {tieneSeleccion && <span className="badge badge-info">Selección manual</span>}
                        <Icono d="m19.5 8.25-7.5 7.5-7.5-7.5" className={`h-4 w-4 text-neutro-400 transition-transform ${expandido ? 'rotate-180' : ''}`} />
                      </div>
                    </button>
                    {expandido && (
                      <div className="border-t border-contorno-var bg-superficie px-5 pb-4 pt-3">
                        <ul className="space-y-1.5">
                          {asignados.map((a) => {
                            const badge = estadoBadge(a.estado);
                            const puedeCalificar = a.estado === 'aceptada' && datos.fecha <= new Date().toISOString().slice(0, 10);
                            const miPuntaje = califPersonas.datos?.find((c) => c.convocatoria_id === a.convocatoria_id)?.puntaje ?? 0;
                            return (
                              <li key={a.convocatoria_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-sm border border-contorno-var">
                                <div className="flex items-center gap-2">
                                  <div className={`h-1.5 w-1.5 rounded-full ${a.estado === 'aceptada' ? 'bg-estado-ok' : a.estado === 'rechazada' ? 'bg-estado-error' : 'bg-estado-pendiente'}`} />
                                  <span className="text-neutro-700">{a.persona_apellido} {a.persona_nombre}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  {puedeCalificar && (
                                    <div className="flex items-center gap-0.5" title="Tu calificación (solo la ve tu local)">
                                      {[1, 2, 3, 4, 5].map((n) => (
                                        <button
                                          key={n}
                                          type="button"
                                          className={`text-base leading-none ${n <= miPuntaje ? 'text-amber-400' : 'text-neutro-300 hover:text-amber-300'}`}
                                          onClick={async () => {
                                            try {
                                              await apiPost(`/api/convocatorias/${a.convocatoria_id}/calificar-persona`, { puntaje: n });
                                              califPersonas.recargar();
                                            } catch (e2) { alert(e2 instanceof Error ? e2.message : 'Error'); }
                                          }}
                                        >★</button>
                                      ))}
                                    </div>
                                  )}
                                  <span className={`badge ${badge.clase}`}>{badge.texto}</span>
                                </div>
                              </li>
                            );
                          })}
                          {Array.from({ length: faltan }, (_, i) => (
                            <li key={`vacante-${i}`} className="flex items-center justify-between rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm">
                              <span className="text-amber-600 italic">Auto-asignado</span>
                              <span className="badge badge-pendiente">Falta confirmar</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Edit form (if editing) */}
          {editando && (
            <form onSubmit={guardarEdicion} className="rounded-2xl border border-contorno-var bg-white p-5 space-y-4">
              <h3 className="text-base font-semibold text-neutro-800">Editar solicitud</h3>
              <label className="block">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Salón</span>
                <select className="campo" required value={form.salon_id} onChange={(e) => setForm({ ...form, salon_id: e.target.value })}>
                  {salonesRes.datos?.map((s) => (
                    <option key={s.id} value={s.id}>{s.nombre}</option>
                  ))}
                </select>
              </label>
              <div className="grid grid-cols-3 gap-4">
                <label className="block">
                  <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Fecha</span>
                  <input className="campo" type="date" required value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Inicio</span>
                  <input className="campo" type="time" required value={form.hora_inicio} onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })} />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Fin</span>
                  <input className="campo" type="time" required value={form.hora_fin} onChange={(e) => setForm({ ...form, hora_fin: e.target.value })} />
                </label>
              </div>
              <label className="block">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Descripción</span>
                <textarea className="campo" rows={2} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
              </label>
              <div>
                <h4 className="text-sm font-semibold text-neutro-700 mb-3">Personas asignadas</h4>
                {cargandoPersonas ? (
                  <p className="text-sm text-neutro-400">Cargando personas disponibles…</p>
                ) : (
                  <div className="space-y-3">
                    {form.puestos.map((p) => {
                      const personas = personasPorCategoria[p.categoria_id] || [];
                      const catNombre = datos.puestos.find(pt => pt.categoria_id === p.categoria_id)?.categoria_nombre || p.categoria_id;
                      return (
                        <div key={p.categoria_id} className="rounded-xl border border-contorno-var bg-superficie p-4">
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-sm font-semibold text-neutro-700">{catNombre}</span>
                            <span className="text-xs font-medium text-neutro-400">{p.personas_seleccionadas.length}/{p.cantidad} seleccionadas</span>
                          </div>
                          {personas.length > 0 ? (
                            <ul className="space-y-1.5">
                              {personas.map((per) => {
                                const seleccionada = p.personas_seleccionadas.includes(per.id);
                                const enOtraCategoria = form.puestos.some(
                                  ot => ot.categoria_id !== p.categoria_id && ot.personas_seleccionadas.includes(per.id)
                                );
                                const deshabilitado = enOtraCategoria && !seleccionada;
                                return (
                                  <li key={per.id}>
                                    <button type="button" disabled={deshabilitado} onClick={() => togglePersona(p.categoria_id, per.id)} className={`w-full flex items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-all ${seleccionada ? 'bg-primario-100 border border-primario-400 text-primario-700' : deshabilitado ? 'bg-neutro-100 text-neutro-400 cursor-not-allowed line-through' : per.disponible ? 'bg-white border border-contorno-var hover:border-primario-200 text-neutro-700' : 'bg-neutro-100 text-neutro-400 line-through'}`}>
                                      <span className="font-medium">{per.apellido} {per.nombre}</span>
                                      <span className="text-xs">{seleccionada ? '✓ Seleccionado' : deshabilitado ? 'En otra categoría' : per.disponible ? `★ ${per.calificacion}` : per.motivo}</span>
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          ) : (
                            <p className="text-xs text-neutro-400">No hay personas disponibles</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
                <p className="text-xs font-medium text-amber-700">Si modificás fecha, hora o salón, las confirmaciones se resetearán.</p>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={enviando} className="boton bg-primario-400 text-white hover:bg-primario-500">
                  {enviando ? 'Guardando…' : 'Guardar cambios'}
                </button>
                <button type="button" onClick={() => setEditando(false)} className="boton border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100">
                  Cancelar
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Sidebar */}
        <div className="w-full lg:w-72 space-y-4 shrink-0">
          {/* Estado + acciones */}
          <div className="rounded-2xl border border-contorno-var bg-white p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-2.5 w-2.5 rounded-full bg-primario-400" />
              <span className="text-sm font-semibold text-neutro-800 capitalize">{datos.estado.replace('_', ' ')}</span>
            </div>
            <div className="space-y-2">
              {puedeEditar && !editando && (
                <button onClick={iniciarEdicion} className="w-full boton border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100 text-sm">
                  <Icono d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" className="h-4 w-4" />
                  Editar solicitud
                </button>
              )}
              {puedeCancelar && (
                <button onClick={cancelar} className="w-full boton border border-red-200 bg-white text-red-600 hover:bg-red-50 text-sm">
                  <Icono d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" className="h-4 w-4" />
                  Cancelar solicitud
                </button>
              )}
            </div>
          </div>

          {/* Notas del salón */}
          {datos.salon.notas && (
            <div className="rounded-2xl border border-contorno-var bg-white p-5">
              <div className="flex items-center gap-2 mb-2">
                <Icono d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" className="h-4 w-4 text-neutro-400" />
                <p className="text-xs font-bold uppercase tracking-wide text-neutro-400">Notas del salón</p>
              </div>
              <p className="text-sm text-neutro-600">{datos.salon.notas}</p>
            </div>
          )}

          {/* Contacto */}
          <div className="rounded-2xl border border-contorno-var bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <Icono d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" className="h-4 w-4 text-neutro-400" />
              <p className="text-xs font-bold uppercase tracking-wide text-neutro-400">Contacto</p>
            </div>
            <p className="text-sm text-neutro-600">{usuario?.email}</p>
          </div>

          {/* Calificación */}
          {datos.estado === 'finalizada' && (
            <div className="rounded-2xl border border-contorno-var bg-white p-5">
              <h3 className="text-sm font-semibold text-neutro-800 mb-3">Calificar servicio</h3>
              {califRes.datos ? (
                <div>
                  <div className="flex gap-1 text-2xl">
                    {[1,2,3,4,5].map((n) => (
                      <span key={n} className={n <= califRes.datos!.calificacion ? 'text-amber-400' : 'text-neutro-200'}>★</span>
                    ))}
                  </div>
                  {califRes.datos.comentario && <p className="mt-3 text-sm text-neutro-600 italic">"{califRes.datos.comentario}"</p>}
                  <p className="mt-2 text-xs text-neutro-400">Calificado el {formatFecha(califRes.datos.creada_en)}</p>
                </div>
              ) : (
                <form onSubmit={enviarCalificacion} className="space-y-3">
                  <div className="flex gap-1 text-3xl">
                    {[1,2,3,4,5].map((n) => (
                      <button key={n} type="button" onClick={() => setCal(n)} className={`transition-colors ${n <= cal ? 'text-amber-400' : 'text-neutro-200 hover:text-amber-200'}`}>★</button>
                    ))}
                  </div>
                  <textarea className="campo" rows={2} placeholder="Comentario (opcional)" value={comentario} onChange={(e) => setComentario(e.target.value)} />
                  <button type="submit" disabled={enviando || cal < 1} className="w-full boton bg-primario-400 text-white hover:bg-primario-500">
                    Enviar calificación
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
